import { useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { bffApi } from "../../../autogen/bff-api";
import { httpStatusOf, isNotFoundError } from "../../errors/isNotFoundError";
import { useApiError } from "../../errors/useApiError";
import { useAppDispatch } from "../../redux/hooks";

const UNAVAILABLE_TOAST_ID = "document-unavailable";

const isUnavailable = (e: unknown): boolean => isNotFoundError(e) || httpStatusOf(e) === 403;

export const useDocumentDownloader = () => {
  const dispatch = useAppDispatch();
  const displayer = useApiError();
  const toast = useToast();
  const { t } = useTranslation();

  const reportFailure = (e: unknown) => {
    if (isUnavailable(e)) {
      if (!toast.isActive(UNAVAILABLE_TOAST_ID)) {
        toast({
          id: UNAVAILABLE_TOAST_ID,
          status: "warning",
          title: t("This document is no longer available"),
          description: t("It may have been removed, or you may no longer have access to it."),
          isClosable: true,
        });
      }
    } else {
      displayer.trigger(e);
    }
    throw e;
  };

  const downloadSignedDocument = async (documentId: string): Promise<string | null> => {
    const postRequestResult = await dispatch(
      bffApi.endpoints.getSignedDocumentDownloadLink.initiate({
        docId: documentId,
      })
    )
      .unwrap()
      .catch(reportFailure);
    return postRequestResult.url;
  };

  const downloadDocument = async (documentId: string): Promise<string> => {
    const postRequestResult = await dispatch(
      bffApi.endpoints.getDownloadLink.initiate({
        docId: documentId,
      })
    )
      .unwrap()
      .catch(reportFailure);
    return postRequestResult.url;
  };

  const getDownloadLink = async (documentId: string, isSignedDocument: boolean): Promise<string | null> => {
    if (isSignedDocument) {
      return await downloadSignedDocument(documentId);
    } else {
      return await downloadDocument(documentId);
    }
  };

  // Q&A attachments are authorized per sourcing event (thread membership), so they use a dedicated
  // event-scoped endpoint rather than the generic document download link.
  const getSeV2AttachmentDownloadLink = async (eventId: string, documentId: string): Promise<string | null> => {
    const result = await dispatch(bffApi.endpoints.getSeV2AttachmentDownloadLink.initiate({ eventId, documentId }))
      .unwrap()
      .catch(reportFailure);
    return result.url;
  };

  return {
    getDownloadLink,
    getSeV2AttachmentDownloadLink,
  };
};
